import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Cloud,
  Loader2,
  Mail,
  Plus,
  Search,
  Trash2,
  UserCog,
  Users,
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ActionOverflowMenu,
  ActionOverflowMenuItem,
  CollapsedBrandLogo,
} from "@doculegis/shared";
import { cn } from "@/lib/utils";
import {
  APPS,
  APPS_HOME_PATH,
  filterNavByPermission,
  getActiveApp,
  SUITE_TITLE,
} from "@/lib/apps";
import { useRagNavConfig } from "@/apps/rag/hooks/useRagData";
import { useSidebarDeleteChat } from "@/apps/rag/hooks/useSidebarDeleteChat";
import { useSidebarNewChat } from "@/apps/rag/hooks/useSidebarNewChat";
import {
  resolveKmBackLink,
} from "@/apps/rag/rag-nav";
import { resolveRegulatoryBackLink } from "@/apps/regulatory/regulatory-nav";
import { useRegulatoryNavConfig } from "@/apps/regulatory/hooks/useRegulatoryNavConfig";
import { useChatProcessNotifications } from "@/apps/chat/hooks/useChatProcessNotifications";
import { useSupervisorChatNav } from "@/apps/chat/hooks/useSupervisorChatNav";
import { requestNewSupervisorChat } from "@/apps/chat/supervisor-chat-nav";
import { BrandLogo } from "@/components/common/BrandLogo";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { UserMenu } from "@/components/common/UserMenu";
import { ChangePasswordDialog } from "@/components/common/ChangePasswordDialog";
import { useAuth } from "@/contexts/AuthContext";
import { resolveOrgBackLink } from "@/lib/org-back-nav";
import { resolveSettingsBackLink } from "@/lib/settings-back-nav";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";

interface SidebarProps {
  isCollapsed: boolean;
  mobileOpen?: boolean;
  onMobileNavClose?: () => void;
}

function navLinkClassName(isCollapsed: boolean, isActive: boolean) {
  return cn(
    "flex items-center rounded-lg px-3 py-2 text-sm transition-all",
    isActive
      ? "border-l-2 border-[var(--nav-active-border)] bg-[var(--nav-active-bg)] text-[#0b1120] dark:border-violet-400 dark:bg-indigo-500/15 dark:text-white"
      : "text-[var(--nav-fg)] hover:bg-[var(--lm-hover)] hover:text-[#0b1120] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white",
    isCollapsed ? "justify-center px-2" : "gap-2.5"
  );
}

function recentNavLinkClassName(isActive: boolean) {
  return cn(
    "block rounded-lg px-3 py-2 text-sm transition-colors",
    isActive
      ? "bg-[var(--nav-sub-active-bg)] text-[#7c3aed] dark:bg-violet-950/80 dark:text-violet-200"
      : "text-[#576274] hover:text-[#101828] dark:text-slate-400 dark:hover:text-slate-300",
  );
}

const sidebarOutlineActionClassName =
  "flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--outline-border)] bg-white px-3 py-2 text-xs text-[#101828] shadow-[var(--shadow-card)] transition-colors hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-transparent dark:text-white dark:shadow-none dark:hover:border-violet-500/35 dark:hover:bg-white/5";

function isAppsDashboardPath(path: string): boolean {
  const normalized = path.replace(/\/+$/, "") || "/";
  return normalized === APPS_HOME_PATH;
}

function isAppNavActive(appId: string, pathname: string, isActive: boolean): boolean {
  if (appId === "chat") {
    const normalized = pathname.replace(/\/+$/, "") || "/";
    return normalized === "/" || normalized === "/chat" || normalized.startsWith("/chat/");
  }
  return isActive;
}

function SidebarBackLink({
  to,
  label,
  isCollapsed,
  onMobileNavClose,
}: {
  to: string;
  label: string;
  isCollapsed: boolean;
  onMobileNavClose?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={() => {
        if (isAppsDashboardPath(to)) onMobileNavClose?.();
      }}
      className={cn(
        "flex items-center rounded-lg px-3 py-2 text-sm text-[var(--nav-fg)] transition-all hover:bg-[var(--lm-hover)] hover:text-[#0b1120] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white",
        isCollapsed ? "justify-center px-2" : "gap-2.5"
      )}
      title={label}
    >
      <ArrowLeft className="h-4 w-4 shrink-0" />
      {!isCollapsed ? <span>{label}</span> : null}
    </Link>
  );
}

function SidebarSectionHeader({
  label,
  isCollapsed,
  variant = "default",
}: {
  label: string;
  isCollapsed: boolean;
  variant?: "default" | "muted";
}) {
  if (isCollapsed) return null;
  return (
    <p
      className={cn(
        "px-2 text-xs font-medium uppercase tracking-[0.12em]",
        variant === "muted"
          ? "pb-1.5 pt-4 text-[#576274] dark:text-slate-500"
          : "pb-2 pt-3 text-[#7c3aed] opacity-90 dark:text-violet-300/90",
      )}
    >
      {label}
    </p>
  );
}

export function Sidebar({ isCollapsed, mobileOpen = false, onMobileNavClose }: SidebarProps) {
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const { pathname } = useLocation();
  const { orgRole, enabledApps, hasPermission } = useAuth();
  const activeApp = getActiveApp(pathname);
  const isOrgRoute =
    pathname === "/users" ||
    pathname.startsWith("/users/") ||
    pathname.startsWith("/organization/");
  const isSettingsRoute = pathname.startsWith("/settings/");
  const isOrgContext = isOrgRoute && !activeApp;
  const isSettingsContext = isSettingsRoute && !activeApp;
  const brandTitle = activeApp
    ? activeApp.name
    : isOrgContext
      ? "Organization"
      : isSettingsContext
        ? "Settings"
        : SUITE_TITLE;
  const visibleApps = APPS.filter(
    (app) => enabledApps.includes(app.id as (typeof enabledApps)[number]) && hasPermission("app", app.id),
  );
  const ragNavConfig = useRagNavConfig();
  const regulatoryNavConfig = useRegulatoryNavConfig();
  const supervisorChatNavConfig = useSupervisorChatNav();
  const { badgeByConversationId } = useChatProcessNotifications();
  const { createNewChat, creating, canCreateChat } = useSidebarNewChat(activeApp?.id);
  const {
    requestDeleteChat,
    confirmDeleteChat,
    cancelDeleteChat,
    pendingDelete,
    deletingId,
    canDeleteChat,
  } = useSidebarDeleteChat(activeApp?.id);
  const dynamicNav =
    activeApp?.id === "rag"
      ? ragNavConfig
      : activeApp?.id === "regulatory"
        ? regulatoryNavConfig
        : activeApp?.id === "chat"
          ? supervisorChatNavConfig
          : null;
  const [subItemsQuery, setSubItemsQuery] = useState("");
  useEffect(() => {
    setSubItemsQuery("");
  }, [activeApp?.id, dynamicNav?.subItemsSearchable]);
  const filteredSubItems = useMemo(() => {
    const items = dynamicNav?.subItems ?? [];
    if (!dynamicNav?.subItemsSearchable) return items;
    const q = subItemsQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [dynamicNav?.subItems, dynamicNav?.subItemsSearchable, subItemsQuery]);
  const rawNav = dynamicNav?.items ?? activeApp?.nav ?? [];
  const appNav = filterNavByPermission(rawNav, hasPermission);
  const backLink =
    activeApp?.id === "rag"
      ? resolveKmBackLink(pathname)
      : activeApp?.id === "regulatory"
        ? resolveRegulatoryBackLink(pathname)
        : { to: APPS_HOME_PATH, label: "Back to apps" };
  const orgBackLink = isOrgContext ? resolveOrgBackLink(pathname) : null;
  const settingsBackLink = isSettingsContext ? resolveSettingsBackLink(pathname) : null;

  const compact = isCollapsed && !mobileOpen;
  const closeMobileNav = () => onMobileNavClose?.();

  return (
    <>
      <aside
        className={cn(
          "flex h-full flex-col border-r transition-all duration-200 border-[rgba(15,23,42,0.09)] bg-[var(--sidebar-bg)] dark:border-white/10 dark:bg-slate-900",
          "absolute inset-y-0 left-0 z-50 w-[min(260px,85vw)] lg:static lg:z-auto",
          isCollapsed ? "lg:w-[78px]" : "lg:w-[260px]",
          mobileOpen ? "translate-x-0" : "-translate-x-full pointer-events-none lg:pointer-events-auto lg:translate-x-0"
        )}
      >
        <div
          className={cn(
            "flex shrink-0 items-center border-b border-[rgba(15,23,42,0.09)] dark:border-white/10",
            compact
              ? "min-h-12 items-center justify-center px-2 py-2 text-[#0b1120] dark:text-white"
              : "min-h-12 items-center px-5 py-2.5"
          )}
        >
          {!compact ? (
            <BrandLogo size="sm" title={brandTitle} showBeta />
          ) : (
            <CollapsedBrandLogo />
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-3">
          {activeApp ? (
            <nav className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 space-y-1">
              <SidebarBackLink
                to={backLink.to}
                label={backLink.label}
                isCollapsed={compact}
                onMobileNavClose={onMobileNavClose}
              />
              {dynamicNav?.sectionLabel && dynamicNav.sectionLabel !== activeApp.name ? (
                <SidebarSectionHeader
                  label={dynamicNav.sectionLabel}
                  isCollapsed={compact}
                />
              ) : null}
              {appNav.length > 0 ? (
                <div className="space-y-1">
                  {appNav.map((item) => (
                    <NavLink
                      key={item.to + item.label}
                      to={item.to}
                      end={item.end}
                      onClick={closeMobileNav}
                      className={({ isActive }) => navLinkClassName(compact, isActive)}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!compact ? <span>{item.label}</span> : null}
                    </NavLink>
                  ))}
                </div>
              ) : null}
              </div>
              {dynamicNav?.subItems?.length ? (
                <>
                  <div className="shrink-0">
                    <SidebarSectionHeader
                      label={dynamicNav.subSectionLabel ?? "Recent"}
                      isCollapsed={compact}
                      variant="muted"
                    />
                    {dynamicNav.subItemsSearchable && !compact ? (
                      <div className="relative mb-1.5 px-0.5">
                        <Search
                          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#576274] dark:text-slate-500"
                          aria-hidden
                        />
                        <input
                          type="search"
                          value={subItemsQuery}
                          onChange={(event) => setSubItemsQuery(event.target.value)}
                          placeholder="Search chats"
                          aria-label="Search chats"
                          className="w-full rounded-lg border border-[rgba(15,23,42,0.09)] bg-[var(--lm-surface)] py-1.5 pl-8 pr-2.5 text-sm text-[#0b1120] placeholder:text-[#576274] outline-none focus:border-[var(--nav-active-border)] dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-violet-400"
                        />
                      </div>
                    ) : null}
                  </div>
                  {!compact ? (
                    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-0.5">
                      {filteredSubItems.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-[#576274] dark:text-slate-500">
                          No matching chats
                        </p>
                      ) : (
                        filteredSubItems.map((item) => {
                          const processBadge =
                            activeApp?.id === "chat" ? badgeByConversationId.get(item.id) : undefined;
                          return (
                          <div key={item.to} className="group relative">
                            <NavLink
                              to={item.to}
                              onClick={closeMobileNav}
                              className={({ isActive }) => recentNavLinkClassName(isActive)}
                            >
                              <span className="flex min-w-0 items-center gap-1.5 pr-6">
                                {processBadge ? (
                                  <span
                                    className={cn(
                                      "inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
                                      processBadge.status === "failed"
                                        ? "bg-[var(--tint-red-bg,rgba(239,68,68,0.12))] text-[var(--tint-red-fg)] dark:bg-red-500/15 dark:text-red-300"
                                        : "bg-[rgba(124,58,237,0.12)] text-[#7c3aed] dark:bg-violet-500/20 dark:text-violet-200",
                                    )}
                                    title={
                                      processBadge.count > 1
                                        ? `${processBadge.count} processes ${processBadge.status === "failed" ? "failed" : "finished"}`
                                        : processBadge.status === "failed"
                                          ? "Process failed"
                                          : "Process finished"
                                    }
                                  >
                                    {processBadge.status === "failed" ? (
                                      <AlertCircle className="h-2.5 w-2.5" aria-hidden />
                                    ) : (
                                      <Check className="h-2.5 w-2.5" aria-hidden />
                                    )}
                                    <span>
                                      {processBadge.status === "failed" ? "Failed" : "Finished"}
                                      {processBadge.count > 1 ? ` · ${processBadge.count}` : ""}
                                    </span>
                                  </span>
                                ) : null}
                                <span className="block min-w-0 truncate">{item.label}</span>
                              </span>
                            </NavLink>
                            {canDeleteChat ? (
                              <ActionOverflowMenu
                                className="absolute right-0 top-1/2 -translate-y-1/2 opacity-70 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                              >
                                <ActionOverflowMenuItem
                                  icon={deletingId === item.id ? Loader2 : Trash2}
                                  label={deletingId === item.id ? "Deleting…" : "Delete chat"}
                                  variant="destructive"
                                  disabled={deletingId !== null}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    requestDeleteChat(item);
                                  }}
                                />
                              </ActionOverflowMenu>
                            ) : null}
                          </div>
                          );
                        })
                      )}
                    </div>
                  ) : null}
                </>
              ) : null}
              {dynamicNav?.footerAction &&
              !compact &&
              (dynamicNav.footerAction.createChat ? canCreateChat : true) ? (
                <div className="shrink-0 pb-3">
                {dynamicNav.footerAction.createChat ? (
                  <button
                    type="button"
                    onClick={() => {
                      void createNewChat(dynamicNav.footerAction!.createChat!).then(closeMobileNav);
                    }}
                    disabled={creating}
                    className={cn(sidebarOutlineActionClassName, "mx-3 mt-3 w-[calc(100%-1.5rem)]")}
                  >
                    {creating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    {dynamicNav.footerAction.label}
                  </button>
                ) : (
                  <NavLink
                    to={dynamicNav.footerAction.to}
                    onClick={() => {
                      requestNewSupervisorChat();
                      closeMobileNav();
                    }}
                    className={cn(sidebarOutlineActionClassName, "mx-3 mt-3 w-[calc(100%-1.5rem)]")}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {dynamicNav.footerAction.label}
                  </NavLink>
                )}
                </div>
              ) : null}
            </nav>
          ) : (
            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto">
              {settingsBackLink ? (
                <SidebarBackLink
                  to={settingsBackLink.to}
                  label={settingsBackLink.label}
                  isCollapsed={compact}
                  onMobileNavClose={onMobileNavClose}
                />
              ) : orgBackLink ? (
                <SidebarBackLink
                  to={orgBackLink.to}
                  label={orgBackLink.label}
                  isCollapsed={compact}
                  onMobileNavClose={onMobileNavClose}
                />
              ) : null}
              <SidebarSectionHeader label="Applications" isCollapsed={compact} />
              {visibleApps.map((app) => (
                <NavLink
                  key={app.id}
                  to={app.entryPath ?? app.basePath}
                  onClick={closeMobileNav}
                  className={({ isActive }) =>
                    navLinkClassName(compact, isAppNavActive(app.id, pathname, isActive))
                  }
                >
                  <app.icon className="h-4 w-4 shrink-0" />
                  {!compact ? <span>{app.name}</span> : null}
                </NavLink>
              ))}
              <SidebarSectionHeader label="Settings" isCollapsed={compact} />
              <NavLink
                to="/settings/google-drive"
                onClick={closeMobileNav}
                className={({ isActive }) => navLinkClassName(compact, isActive)}
              >
                <GoogleDriveIcon className="h-4 w-4 shrink-0" />
                {!compact ? <span>Google Drive</span> : null}
              </NavLink>
              <NavLink
                to="/settings/email"
                onClick={closeMobileNav}
                className={({ isActive }) => navLinkClassName(compact, isActive)}
              >
                <Mail className="h-4 w-4 shrink-0" />
                {!compact ? <span>Email inbox</span> : null}
              </NavLink>
              {orgRole === "owner" || orgRole === "admin" || orgRole === "manager" ? (
                <>
                  <SidebarSectionHeader label="Organization" isCollapsed={compact} />
                  {orgRole === "owner" || orgRole === "admin" ? (
                    <>
                      <NavLink
                        to="/users"
                        end
                        onClick={closeMobileNav}
                        className={({ isActive }) => navLinkClassName(compact, isActive)}
                      >
                        <Users className="h-4 w-4 shrink-0" />
                        {!compact ? <span>Users</span> : null}
                      </NavLink>
                      <NavLink
                        to="/organization/roles"
                        onClick={closeMobileNav}
                        className={({ isActive }) => navLinkClassName(compact, isActive)}
                      >
                        <UserCog className="h-4 w-4 shrink-0" />
                        {!compact ? <span>Roles</span> : null}
                      </NavLink>
                    </>
                  ) : null}
                  <NavLink
                    to="/organization/integrations"
                    onClick={closeMobileNav}
                    className={({ isActive }) => navLinkClassName(compact, isActive)}
                  >
                    <Cloud className="h-4 w-4 shrink-0" />
                    {!compact ? <span>Integrations</span> : null}
                  </NavLink>
                </>
              ) : null}
            </nav>
          )}
        </div>

        <div className="mt-auto border-t border-[rgba(15,23,42,0.09)] p-3 dark:border-white/10">
          <UserMenu
            isCollapsed={compact}
            onOpenChangePassword={() => setIsChangePasswordOpen(true)}
          />
        </div>
      </aside>

      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <ConfirmDialog
        isOpen={pendingDelete != null}
        onClose={cancelDeleteChat}
        onConfirm={confirmDeleteChat}
        title="Delete chat"
        description={
          pendingDelete
            ? `Delete "${pendingDelete.label}"? This cannot be undone.`
            : "Delete this chat? This cannot be undone."
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deletingId !== null}
      />

    </>
  );
}
